import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitiveHoveredItem,
  SeriesAttachedParameter,
  Time,
  UTCTimestamp,
} from "lightweight-charts";
import type { ChartMarkup } from "./chart-models.js";
import type { MarketTimeline } from "./market-timeline.js";

interface PositionedMarkup {
  readonly item: ChartMarkup;
  readonly number: number;
  readonly startTime: number;
  readonly endTime: number;
}

/** Native series primitive: every paint uses the chart's current time/price coordinates. */
export class ChartAnnotationsPrimitive implements ISeriesPrimitive<Time> {
  private attachment: SeriesAttachedParameter<Time> | undefined;
  private items: readonly PositionedMarkup[] = [];
  private selectedId: string | undefined;
  private readonly views: readonly IPrimitivePaneView[];

  public constructor(private readonly timeline: MarketTimeline) {
    const renderer: IPrimitivePaneRenderer = {
      drawBackground: (target) => {
        target.useMediaCoordinateSpace(({ context }) => {
          for (const row of this.items) {
            const position = this.coordinates(row);
            if (position === undefined || row.item.kind !== "zone") continue;
            context.save();
            context.globalAlpha = row.item.id === this.selectedId ? 0.22 : 0.1;
            context.fillStyle = row.item.color;
            context.fillRect(
              position.left,
              position.top,
              position.right - position.left,
              Math.max(2, position.bottom - position.top),
            );
            context.restore();
          }
        });
      },
      draw: (target) => {
        target.useMediaCoordinateSpace(({ context }) => {
          for (const row of this.items) {
            const position = this.coordinates(row);
            if (position === undefined) continue;
            context.save();
            context.strokeStyle = row.item.color;
            context.fillStyle = row.item.color;
            context.lineWidth = row.item.id === this.selectedId ? 3 : 1.5;
            if (row.item.kind === "event") {
              context.beginPath();
              context.moveTo(position.badgeX, position.top);
              context.lineTo(position.badgeX, position.badgeY);
              context.stroke();
            } else {
              context.setLineDash([6, 4]);
              context.beginPath();
              context.moveTo(position.left, position.top);
              context.lineTo(position.right, position.top);
              if (row.item.kind === "zone") {
                context.moveTo(position.left, position.bottom);
                context.lineTo(position.right, position.bottom);
              }
              context.stroke();
              context.setLineDash([]);
            }
            context.beginPath();
            context.arc(position.badgeX, position.badgeY, 11, 0, Math.PI * 2);
            context.fill();
            context.fillStyle = "#06110d";
            context.font = "bold 12px sans-serif";
            context.textAlign = "center";
            context.textBaseline = "middle";
            context.fillText(String(row.number), position.badgeX, position.badgeY);
            context.restore();
          }
        });
      },
    };
    this.views = [{ zOrder: () => "top", renderer: () => renderer }];
  }

  public setAnnotations(annotations: readonly ChartMarkup[], selectedId?: string): void {
    this.selectedId = selectedId;
    this.items = annotations.flatMap((item, index) => {
      const start = this.timeline.bars.find((row) => row.bar.sessionDate === item.startSessionDate);
      const end = [...this.timeline.bars]
        .reverse()
        .find((row) => row.bar.sessionDate === item.endSessionDate);
      return start === undefined || end === undefined
        ? []
        : [{ item, number: index + 1, startTime: start.chartTime, endTime: end.chartTime }];
    });
    this.attachment?.requestUpdate();
  }

  public attached(attachment: SeriesAttachedParameter<Time>): void {
    this.attachment = attachment;
  }
  public detached(): void {
    this.attachment = undefined;
  }
  public paneViews(): readonly IPrimitivePaneView[] {
    return this.views;
  }

  public autoscaleInfo() {
    if (this.items.length === 0) return null;
    return {
      priceRange: {
        minValue: Math.min(...this.items.map((row) => row.item.lowerPrice)),
        maxValue: Math.max(...this.items.map((row) => row.item.upperPrice)),
      },
    };
  }

  public hitTest(x: number, y: number): PrimitiveHoveredItem | null {
    for (const row of [...this.items].reverse()) {
      const position = this.coordinates(row);
      if (position === undefined) continue;
      const distance = Math.hypot(x - position.badgeX, y - position.badgeY);
      if (distance <= 14)
        return {
          externalId: `annotation:${row.item.id}`,
          zOrder: "top",
          cursorStyle: "pointer",
          distance,
          hitTestPriority: 2,
        };
    }
    return null;
  }

  private coordinates(row: PositionedMarkup) {
    const attached = this.attachment;
    if (attached === undefined) return undefined;
    const scale = attached.chart.timeScale();
    const startX = scale.timeToCoordinate(row.startTime as UTCTimestamp);
    const endX = scale.timeToCoordinate(row.endTime as UTCTimestamp);
    const top = attached.series.priceToCoordinate(row.item.upperPrice);
    const bottom = attached.series.priceToCoordinate(row.item.lowerPrice);
    if (startX === null || endX === null || top === null || bottom === null) return undefined;
    const halfBar = scale.options().barSpacing / 2;
    const left = startX - halfBar;
    const right = endX + halfBar;
    if (right < 0 || left > scale.width()) return undefined;
    return {
      left,
      right,
      top,
      bottom,
      badgeX: row.item.kind === "event" ? startX : Math.max(12, left + 12),
      badgeY: row.item.kind === "event" ? Math.max(12, top - 20) : top,
    };
  }
}
